import { useEffect, useRef, useState } from 'react'
import {
  Avatar,
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded'
import CloseRounded from '@mui/icons-material/CloseRounded'
import SendRounded from '@mui/icons-material/SendRounded'
import { useAppData } from '../contexts/AppDataContext'
import { useConciergeEnabled } from '../hooks/useConciergeEnabled'
import { conciergeService, type ConciergeMessage } from '../services/conciergeService'
import { CONCIERGE_NAME } from '../config/concierge'
import SafeHtml from './SafeHtml'
import { renderMarkdown } from '../utils/miniMarkdown'

/**
 * Floating "Ask {CONCIERGE_NAME}" chat widget.
 *
 * Renders nothing unless the event's Concierge is enabled (both gates), so the
 * disable flag removes it entirely — no dangling entry point. Streams answers
 * token-by-token via the SSE-over-POST client.
 */
export default function ConciergeWidget() {
  const { event } = useAppData()
  const eventId = event?.id
  const enabled = useConciergeEnabled(eventId)

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ConciergeMessage[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const conversationId = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Load prior turns once, when the panel is first opened.
  const loadedRef = useRef(false)
  useEffect(() => {
    if (!open || loadedRef.current || !eventId) return
    loadedRef.current = true
    conciergeService.getHistory(eventId).then((h) => {
      if (h.messages.length) {
        setMessages(h.messages)
        conversationId.current = h.conversationId
      }
    })
  }, [open, eventId])

  // Autoscroll to the latest message.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages, busy])

  // Abort any in-flight stream on unmount.
  useEffect(() => () => abortRef.current?.abort(), [])

  if (!enabled || !eventId) return null

  const send = () => {
    const question = input.trim()
    if (!question || busy) return
    setInput('')
    setBusy(true)
    setMessages((m) => [
      ...m,
      { role: 'user', content: question },
      { role: 'assistant', content: '' },
    ])

    const { controller, done } = conciergeService.ask(
      eventId,
      question,
      conversationId.current,
      {
        onMeta: (cid) => {
          if (cid) conversationId.current = cid
        },
        onToken: (text) => {
          setMessages((m) => {
            const next = [...m]
            const last = next[next.length - 1]
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, content: last.content + text }
            }
            return next
          })
        },
        onError: (msg) => {
          setMessages((m) => {
            const next = [...m]
            const last = next[next.length - 1]
            if (last && last.role === 'assistant' && !last.content) {
              next[next.length - 1] = { ...last, content: `⚠️ ${msg}` }
            } else {
              next.push({ role: 'assistant', content: `⚠️ ${msg}` })
            }
            return next
          })
        },
      },
    )
    abortRef.current = controller
    done.finally(() => setBusy(false))
  }

  return (
    <>
      {!open && (
        <Fab
          color="primary"
          aria-label={`Ask ${CONCIERGE_NAME}`}
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            right: 20,
            bottom: { xs: 88, md: 24 }, // clear the mobile bottom nav
            zIndex: (t) => t.zIndex.drawer + 2,
          }}
        >
          <AutoAwesomeRounded />
        </Fab>
      )}

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            right: { xs: 8, md: 20 },
            left: { xs: 8, md: 'auto' },
            bottom: { xs: 80, md: 24 },
            width: { xs: 'auto', md: 380 },
            height: { xs: '70vh', md: 520 },
            maxHeight: 'calc(100vh - 100px)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: (t) => t.zIndex.drawer + 2,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <AutoAwesomeRounded fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              {CONCIERGE_NAME}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              aria-label="Close"
              sx={{ color: 'inherit' }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            ref={scrollRef}
            sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: 'background.default' }}
          >
            {messages.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 1.5, textAlign: 'center' }}
              >
                Hi, I&apos;m {CONCIERGE_NAME} — ask me about the agenda, speakers, venue,
                sponsors, or your schedule.
              </Typography>
            )}
            <Stack spacing={1.25}>
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} content={m.content} />
              ))}
              {busy &&
                messages[messages.length - 1]?.role === 'assistant' &&
                !messages[messages.length - 1]?.content && (
                  <Box sx={{ pl: 1 }}>
                    <CircularProgress size={16} />
                  </Box>
                )}
            </Stack>
          </Box>

          {/* Composer */}
          <Box
            sx={{
              p: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 1,
              bgcolor: 'background.paper',
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder={`Ask ${CONCIERGE_NAME}…`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              disabled={busy}
              multiline
              maxRows={3}
            />
            <IconButton
              color="primary"
              onClick={send}
              disabled={busy || !input.trim()}
              aria-label="Send"
            >
              <SendRounded />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  )
}

function Bubble({ role, content }: { role: string; content: string }) {
  const isUser = role === 'user'
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      {!isUser && (
        <Avatar sx={{ width: 26, height: 26, mr: 1, bgcolor: 'primary.main' }}>
          <AutoAwesomeRounded sx={{ fontSize: 15 }} />
        </Avatar>
      )}
      <Box
        sx={{
          maxWidth: '78%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          wordBreak: 'break-word',
          bgcolor: isUser ? 'primary.main' : 'background.paper',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          border: isUser ? 'none' : '1px solid',
          borderColor: 'divider',
        }}
      >
        {isUser ? (
          // Attendee input: plain text, preserve their line breaks.
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {content || '…'}
          </Typography>
        ) : content ? (
          // Casey replies in Markdown → render (sanitized by SafeHtml). Tight
          // spacing so the bubble reads like chat, not a document.
          <SafeHtml
            html={renderMarkdown(content)}
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.5,
              '& p': { m: 0, mb: 0.75, '&:last-child': { mb: 0 } },
              '& ul, & ol': { m: 0, mb: 0.75, pl: 2.5 },
              '& li': { mb: 0.25 },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                m: 0,
                mb: 0.5,
                fontSize: '0.95rem',
                fontWeight: 700,
              },
              '& a': { color: 'primary.main' },
              '& code': {
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                bgcolor: 'action.hover',
                px: 0.5,
                borderRadius: 0.5,
              },
              '& pre': {
                bgcolor: 'action.hover',
                p: 1,
                borderRadius: 1,
                overflowX: 'auto',
                '& code': { bgcolor: 'transparent', p: 0 },
              },
              '& blockquote': {
                m: 0,
                mb: 0.75,
                pl: 1,
                borderLeft: '3px solid',
                borderColor: 'divider',
                color: 'text.secondary',
              },
            }}
          />
        ) : (
          <Typography variant="body2">…</Typography>
        )}
      </Box>
    </Box>
  )
}
